import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useDrop, useDrag } from 'react-dnd';
import BoxPlanoModal from './boxPlanoModal';

// Individual DropBox component for each box
const DropBox = ({ line, rowIdx, colIdx, onBoxClick, onProductDrop, onProductRemove }) => {
  const [{ isOver }, drop] = useDrop(() => ({
    accept: 'product',
    drop: (item) => {
      onProductDrop(item.prod, rowIdx, colIdx);
    },
    collect: (monitor) => ({
      isOver: !!monitor.isOver(),
    }),
  }), [rowIdx, colIdx, onProductDrop]);

  // Add drag functionality for existing products in boxes
  const [{ isDragging }, drag] = useDrag(() => ({
    type: 'boxProduct',
    item: { product: line?.product, rowIdx, colIdx },
    canDrag: !!line?.product,
    end: (item, monitor) => {
      if (monitor.didDrop()) {
        onProductRemove(rowIdx, colIdx);
      }
    },
    collect: (monitor) => ({
      isDragging: !!monitor.isDragging(),
    }),
  }), [line?.product, rowIdx, colIdx, onProductRemove]);

  return (
    <div
      ref={drop}
      onClick={() => onBoxClick(line, colIdx)}
      className={`box-plano-drop-zone ${isOver ? 'box-plano-drop-active' : ''}`}
      style={{
        backgroundColor: isOver ? 'rgba(0, 255, 0, 0.3)' : 'transparent',
        border: isOver ? '2px dashed #00ff00' : 'none',
        borderRadius: '5px',
        transition: 'all 0.2s ease',
        position: 'relative',
      }}
    >
      {line?.product && (
        <img
          ref={drag}
          className="box-plano-img"
          src={`${process.env.REACT_APP_SERVER_API}/pictures/${line?.product?.productPictures[0]}/${line.product?.referTo}`}
          alt="product"
          style={{ 
            pointerEvents: 'auto',
            cursor: 'move',
            opacity: isDragging ? 0.5 : 1,
            border: isDragging ? '2px solid #ff6b6b' : 'none',
            transition: 'opacity 0.2s ease, border 0.2s ease'
          }}
        />
      )}

      <div className="box-plano-box" style={{ pointerEvents: 'none' }}>
        {line.id}
      </div>
      {line.type === '3' && <div className="box-plano-linkline"></div>}
      
      {/* Drop indicator overlay */}
      {isOver && (
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 255, 0, 0.2)',
            border: '2px dashed #00ff00',
            borderRadius: '5px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            pointerEvents: 'none',
            zIndex: 10,
          }}
        >
          <span style={{ color: '#00aa00', fontWeight: 'bold', fontSize: '12px' }}>
            Drop Here
          </span>
        </div>
      )}
    </div>
  );
};

export default function BoxPlano({ boxes, name, setBoxes, prods }) {
  const [show, setShow] = useState(false);
  const [data, setData] = useState(null);
  const [floors, setFloors] = useState(0);
  // const [heights, setHeights] = useState([]);

  // Force re-render when boxes structure changes
  const boxKey = useMemo(() => {
    return `${boxes?.rows || 0}-${boxes?.columns || 0}-${boxes?.floors?.length || 0}`;
  }, [boxes?.rows, boxes?.columns, boxes?.floors?.length]);

  const handleBoxClick = useCallback((line, colIdx) => {
    setFloors(colIdx);
    setData(line);
    setShow(true);
  }, []);

  const handleProductDrop = useCallback((product, rowIdx, colIdx) => {
    setBoxes(currentBoxes => {
      // Validate indices before updating
      if (!currentBoxes?.floors?.[rowIdx] || !currentBoxes?.floors?.[rowIdx]?.lines?.[colIdx]) {
        return currentBoxes;
      }
      
      // Update the boxes state with the new product
      const updatedBoxes = JSON.parse(JSON.stringify(currentBoxes));
      updatedBoxes.floors[rowIdx].lines[colIdx].product = product;
      
      return updatedBoxes;
    });
  }, [setBoxes]);

  const handleProductRemove = useCallback((rowIdx, colIdx) => {
    setBoxes(currentBoxes => {
      // Validate indices before removing
      if (!currentBoxes?.floors?.[rowIdx] || !currentBoxes?.floors?.[rowIdx]?.lines?.[colIdx]) {
        return currentBoxes;
      }
      
      // Remove the product from the box
      const updatedBoxes = JSON.parse(JSON.stringify(currentBoxes));
      updatedBoxes.floors[rowIdx].lines[colIdx].product = null;
      
      return updatedBoxes;
    });
  }, [setBoxes]);

  return (
    <div key={boxKey} className="box-plano-div">
      <h5 className="mb-5 mt-2">{name}</h5>
      
      {/* Column Headers */}
      {boxes?.floors?.length > 0 && (
        <div className="box-plano-headers">
          <div className="box-plano-row-label"></div> {/* Empty space for row labels */}
          {boxes.floors[0]?.lines?.map((_, colIdx) => (
            <div key={`${boxes?.rows}-${boxes?.columns}-header-${colIdx}`} className="box-plano-column-header">
              Column {colIdx + 1}
            </div>
          ))}
        </div>
      )}

      {boxes?.floors?.map((floor, rowIdx) => (
        <div key={`${boxes?.rows}-${boxes?.columns}-row-${rowIdx}`} className="box-plano-floors">
          {/* Row Label */}
          <div className="box-plano-row-label">Row {rowIdx + 1}</div>
          
          {floor.lines.map((line, colIdx) => (
            <DropBox
              key={`${boxes?.rows}-${boxes?.columns}-${rowIdx}-${colIdx}-${line.id}`}
              line={line}
              rowIdx={rowIdx}
              colIdx={colIdx}
              onBoxClick={handleBoxClick}
              onProductDrop={handleProductDrop}
              onProductRemove={handleProductRemove}
            />
          ))}
        </div>
      ))}
      <BoxPlanoModal
        prods={prods}
        show={show}
        data={data}
        setBoxes={setBoxes}
        boxes={boxes}
        onHide={() => {
          setShow(false);
        }}
      />
    </div>
  );
}
